import clsx from 'clsx'
import { useMemo } from 'react'
import { useMasterLayout } from 'src/app/context/MasterContext'
import DashboardScreens from 'src/app/modules/dashboard/Screens'
import ProductManagementScreens from 'src/app/modules/product-management/Screens'
import BroadcastScreens from '../../../modules/broadcast/Screen'
import CampaignScreens from '../../../modules/campaign/Screen'
import CustomerScreens from '../../../modules/customer/Screen'
import GeneralConfigScreens from '../../../modules/general-config/Screen'
import UserManagementScreens from '../../../modules/user-management/Screen'
import LocationScreens from 'src/app/modules/location/Screens'
import { UserModel } from '../../../modules/user-management/models/ManageUser.model'
import { ModulePermissionQuery } from '../../../modules/user-management/models/Permission.model'
import { useGetProfile } from '../../../modules/user-management/providers/ManageUser.provider'
import { AsideMenuItem } from './AsideMenuItem'
import { AsideMenuItemWithSub } from './AsideMenuItemWithSub'
import InquiryManagementScreens from 'src/app/modules/inquiry-management/Screens'
import MenuManagementScreens from '../../../modules/menu-management/Screens'
import PolicyManagementScreens from '../../../modules/policy-management/Screen'
import CustomerSurveyScreens from 'src/app/modules/customer-survey/Screens'
import LoyaltyScreens from '../../../modules/loyalty/Screen'
import AuditTrailScreens from 'src/app/modules/audit-trail/Screens'

export interface Menu {
  id?: string
  to?: string
  title: string
  icon?: string
  fontIcon?: string
  hasBullet?: boolean
  children?: Menu[]
  module_permissions?: ModulePermissionQuery
  activePath?: string
  badge?: string
  badgeCircle?: boolean
  hidden?: boolean
  disabled?: boolean
}

const useMenus = (user?: UserModel): Menu[] => {
  return useMemo(
    () =>
      [
        {
          children: [
            {
              to: DashboardScreens.DASHBOARD_USER_DATA_MOBILE.PATH,
              icon: '/media/icons/IconDashboard.svg',
              title: DashboardScreens.DASHBOARD.TITLE,
              module_permissions: DashboardScreens.DASHBOARD.PERMISSION,
              activePath: DashboardScreens.DASHBOARD.PATH
            },
            {
              to: CustomerScreens.CUSTOMER_MANAGEMENT_LIST.PATH,
              icon: '/media/icons/IconCustomer.svg',
              title: CustomerScreens.CUSTOMER_MANAGEMENT_LIST.TITLE,
              activePath: CustomerScreens.CUSTOMER_MANAGEMENT.PATH,
              module_permissions: CustomerScreens.CUSTOMER_MANAGEMENT_LIST.PERMISSION,
            },
            {
              to: ProductManagementScreens.PRODUCT_MANAGEMENT_LIST.PATH,
              icon: '/media/icons/IconProductManagement.svg',
              title: ProductManagementScreens.PRODUCT_MANAGEMENT_LIST.TITLE,
              activePath: ProductManagementScreens.PRODUCT_MANAGEMENT.PATH,
              module_permissions: ProductManagementScreens.PRODUCT_MANAGEMENT.PERMISSION,
            },
            {
              to: CampaignScreens.CAMPAIGN.PATH,
              icon: '/media/icons/IconCampaign.svg',
              title: CampaignScreens.CAMPAIGN.TITLE,
              children: [
                {
                  to: CampaignScreens.LIST_MAIN_BANNER.PATH,
                  activePath: `${CampaignScreens.LIST_MAIN_BANNER.PATH},${CampaignScreens.LIST_FEATURED_BANNER.PATH}`,
                  title: CampaignScreens.LIST_MAIN_BANNER.TITLE,
                  hasBullet: true,
                },
                {
                  to: CampaignScreens.LIST_POPUP_BANNER.PATH,
                  activePath: CampaignScreens.LIST_POPUP_BANNER.PATH,
                  title: CampaignScreens.LIST_POPUP_BANNER.TITLE,
                  hasBullet: true,
                },
              ],
            },
            {
              to: PolicyManagementScreens.LIST_POLICY_MANAGEMENT.PATH,
              icon: '/media/icons/IconPolicyManagement.svg',
              title: PolicyManagementScreens.POLICY_MANAGEMENT.TITLE,
              activePath: PolicyManagementScreens.POLICY_MANAGEMENT.PATH,
              module_permissions: PolicyManagementScreens.POLICY_MANAGEMENT.PERMISSION,
            },
            {
              to: LoyaltyScreens.LOYALTY.PATH,
              icon: '/media/icons/IconReward.svg',
              title: LoyaltyScreens.LOYALTY.TITLE,
              children: [
                {
                  to: LoyaltyScreens.LIST_LOYALTY_REWARD.PATH,
                  activePath: LoyaltyScreens.LIST_LOYALTY_REWARD.PATH,
                  title: LoyaltyScreens.LIST_LOYALTY_REWARD.TITLE,
                  hasBullet: true,
                },
                {
                  to: LoyaltyScreens.LIST_LOYALTY_MISSION.PATH,
                  activePath: LoyaltyScreens.LIST_LOYALTY_MISSION.PATH,
                  title: LoyaltyScreens.LIST_LOYALTY_MISSION.TITLE,
                  hasBullet: true,
                },
                {
                  to: LoyaltyScreens.LIST_LOYALTY_CAMPAIGN.PATH,
                  activePath: LoyaltyScreens.LIST_LOYALTY_CAMPAIGN.PATH,
                  title: LoyaltyScreens.LIST_LOYALTY_CAMPAIGN.TITLE,
                  hasBullet: true,
                },
                {
                  to: LoyaltyScreens.LIST_POINT_MATRIX.PATH,
                  activePath: LoyaltyScreens.LIST_POINT_MATRIX.PATH,
                  title: LoyaltyScreens.LIST_POINT_MATRIX.TITLE,
                  hasBullet: true,
                },
              ],
            },
            {
              to: MenuManagementScreens.LIST_MENU_MANAGEMENT.PATH,
              icon: '/media/icons/IconMenuManagement.svg',
              title: MenuManagementScreens.MENU_MANAGEMENT.TITLE,
              activePath: MenuManagementScreens.MENU_MANAGEMENT.PATH,
              module_permissions: MenuManagementScreens.MENU_MANAGEMENT.PERMISSION,
            },
            {
              to: BroadcastScreens.LIST_BROADCAST.PATH,
              icon: '/media/icons/IconBroadcast.svg',
              title: BroadcastScreens.BROADCAST.TITLE,
              activePath: BroadcastScreens.BROADCAST.PATH,
              module_permissions: BroadcastScreens.BROADCAST.PERMISSION,
            },
            {
              to: LocationScreens.LOCATION.PATH,
              icon: '/media/icons/IconLocation.svg',
              title: LocationScreens.LOCATION.TITLE,
              children: [
                {
                  to: LocationScreens.LIST_WORKSHOP.PATH,
                  activePath: LocationScreens.LIST_WORKSHOP.PATH,
                  title: LocationScreens.LIST_WORKSHOP.TITLE,
                  hasBullet: true,
                },
                {
                  to: LocationScreens.LIST_BRANCHES.PATH,
                  activePath: LocationScreens.LIST_BRANCHES.PATH,
                  title: LocationScreens.LIST_BRANCHES.TITLE,
                  hasBullet: true,
                },
              ],
            },
            {
              to: UserManagementScreens.MANAGE_USER.PATH,
              icon: '/media/icons/IconUser.svg',
              title: UserManagementScreens.MANAGE_USER.TITLE,
              children: [
                {
                  to: UserManagementScreens.LIST_MANAGE_ROLE.PATH,
                  activePath: `${UserManagementScreens.LIST_MANAGE_ROLE.PATH},${UserManagementScreens.DETAIL_MANAGE_ROLE.PATH},${UserManagementScreens.ADD_MANAGE_ROLE.PATH},${UserManagementScreens.EDIT_MANAGE_ROLE.PATH}`,
                  title: UserManagementScreens.LIST_MANAGE_ROLE.TITLE,
                  hasBullet: true,
                },
                {
                  to: UserManagementScreens.LIST_MANAGE_USER.PATH,
                  activePath: `${UserManagementScreens.LIST_MANAGE_USER.PATH},${UserManagementScreens.ADD_MANAGE_USER.PATH}`,
                  title: UserManagementScreens.LIST_MANAGE_USER.TITLE,
                  hasBullet: true,
                },
              ],
            },
            {
              to: CustomerSurveyScreens.CUSTOMER_SURVEY.PATH,
              icon: '/media/icons/IconCustomerSurvey.svg',
              title: CustomerSurveyScreens.CUSTOMER_SURVEY.TITLE,
              children: [
                {
                  to: CustomerSurveyScreens.LIST_GENERAL_SURVEY_CREATION.PATH,
                  activePath: `${CustomerSurveyScreens.LIST_GENERAL_SURVEY_CREATION.PATH},${CustomerSurveyScreens.LIST_CLAIM_SURVEY_CREATION.PATH}`,
                  title: CustomerSurveyScreens.LIST_GENERAL_SURVEY_CREATION.TITLE,
                  hasBullet: true,
                },
                {
                  to: CustomerSurveyScreens.LIST_GENERAL_SURVEY_RESPONSE.PATH,
                  activePath: `${CustomerSurveyScreens.LIST_GENERAL_SURVEY_RESPONSE.PATH},${CustomerSurveyScreens.LIST_CLAIM_SURVEY_RESPONSE.PATH}`,
                  title: CustomerSurveyScreens.LIST_GENERAL_SURVEY_RESPONSE.TITLE,
                  hasBullet: true,
                },
              ],
            },
            {
              to: GeneralConfigScreens.GENERAL_CONFIG.PATH,
              icon: '/media/icons/IconSetting.svg',
              title: GeneralConfigScreens.GENERAL_CONFIG.TITLE,
              children: [
                {
                  to: GeneralConfigScreens.VIEW_TERMS_CONDITIONS.PATH,
                  activePath: GeneralConfigScreens.VIEW_TERMS_CONDITIONS.PATH,
                  title: GeneralConfigScreens.VIEW_TERMS_CONDITIONS.TITLE,
                  hasBullet: true,
                },
                {
                  to: GeneralConfigScreens.VIEW_PRIVACY_POLICY.PATH,
                  activePath: GeneralConfigScreens.VIEW_PRIVACY_POLICY.PATH,
                  title: GeneralConfigScreens.VIEW_PRIVACY_POLICY.TITLE,
                  hasBullet: true,
                },
                {
                  to: GeneralConfigScreens.LIST_FAQ.PATH,
                  activePath: GeneralConfigScreens.LIST_FAQ.PATH,
                  title: GeneralConfigScreens.LIST_FAQ.HEADER_NAME,
                  hasBullet: true,
                },
              ],
            },
            {
              to: InquiryManagementScreens.INQUIRY_MANAGEMENT.PATH,
              icon: '/media/icons/IconInquiry.svg',
              title: InquiryManagementScreens.INQUIRY_MANAGEMENT.TITLE,
              children: [
                {
                  to: InquiryManagementScreens.LIST_NEED_PROCESS_POLICY_RENEWAL.PATH,
                  activePath: `${InquiryManagementScreens.LIST_NEED_PROCESS_POLICY_RENEWAL.PATH},${InquiryManagementScreens.LIST_DONE_PROCESS_POLICY_RENEWAL.PATH},${InquiryManagementScreens.DETAIL_POLICY_RENEWAL.PATH}`,
                  title: InquiryManagementScreens.LIST_NEED_PROCESS_POLICY_RENEWAL.TITLE,
                  hasBullet: true,
                },
                {
                  to: InquiryManagementScreens.LIST_NEED_PROCESS_POLICY_ENDORSEMENT.PATH,
                  activePath: `${InquiryManagementScreens.LIST_NEED_PROCESS_POLICY_ENDORSEMENT.PATH},${InquiryManagementScreens.LIST_DONE_PROCESS_POLICY_ENDORSEMENT.PATH},${InquiryManagementScreens.DETAIL_POLICY_ENDORSEMENT.PATH}`,
                  title: InquiryManagementScreens.LIST_NEED_PROCESS_POLICY_ENDORSEMENT.TITLE,
                  hasBullet: true,
                },
                {
                  to: InquiryManagementScreens.LIST_NEED_PROCESS_PRODUCT_INFO.PATH,
                  activePath: `${InquiryManagementScreens.LIST_NEED_PROCESS_PRODUCT_INFO.PATH},${InquiryManagementScreens.LIST_DONE_PROCESS_PRODUCT_INFO.PATH},${InquiryManagementScreens.DETAIL_PRODUCT_INFO.PATH}`,
                  title: InquiryManagementScreens.LIST_NEED_PROCESS_PRODUCT_INFO.TITLE,
                  hasBullet: true,
                },
              ],
            },
            {
              to: AuditTrailScreens.AUDIT_TRAIL.PATH,
              icon: '/media/icons/IconInquiry.svg',
              title: AuditTrailScreens.AUDIT_TRAIL.TITLE,
              children: [
                {
                  to: AuditTrailScreens.LIST_USER_ACTIVITY.PATH,
                  activePath: AuditTrailScreens.LIST_USER_ACTIVITY.PATH,
                  title: AuditTrailScreens.LIST_USER_ACTIVITY.TITLE,
                  hasBullet: true,
                },
                {
                  to: AuditTrailScreens.LIST_SYSTEM_ACTIVITY.PATH,
                  activePath: AuditTrailScreens.LIST_SYSTEM_ACTIVITY.PATH,
                  title: AuditTrailScreens.LIST_SYSTEM_ACTIVITY.TITLE,
                  hasBullet: true,
                },
              ],
            },
          ],
        },
      ] as Menu[],
    [user]
  )
}

export const GeneratedMenu: React.FC<{menu: Menu}> = ({menu}) => {
  const {minimize} = useMasterLayout()

  if (!menu.to) {
    if (!menu.children || menu.children.length === 0) return null
    return (
      <>
        <div className='block p-0'>
          <div className='px-6 pt-8 pb-2'>
            <span
              className={clsx('tracking-widest text-white uppercase text-fs-10', {
                hidden: minimize,
              })}
            >
              {menu.title}
            </span>
          </div>
        </div>
        {menu.children?.map((child) => {
          return <GeneratedMenu menu={child} key={child.id} />
        })}
      </>
    )
  }
  if (!menu.children)
    return (
      <AsideMenuItem
        to={menu.to}
        icon={menu.icon}
        title={menu.title}
        fontIcon={menu.fontIcon}
        hasBullet={menu.hasBullet}
        activePath={menu.activePath}
        badge={menu.badge}
        badgeCircle={menu.badgeCircle}
        disabled={menu.disabled}
      />
    )

  return (
    <AsideMenuItemWithSub
      to={menu.to}
      title={menu.title}
      fontIcon={menu.fontIcon}
      icon={menu.icon}
      activePath={menu.activePath}
      hasBullet={menu.hasBullet}
      disabled={menu.disabled}
    >
      {menu.children.map((child) => {
        return <GeneratedMenu menu={child} key={child.id} />
      })}
    </AsideMenuItemWithSub>
  )
}

const filterMenus = (
  menus: Menu[] | undefined,
  predicate: (menu: Menu) => boolean
): Menu[] | undefined => {
  const result = menus?.map((menu, index) => ({
    ...menu,
    id: String(index),
    children: filterMenus(menu.children, predicate),
  }))
  return result?.filter((menu) => (!menu.children || menu.children.length > 0) && predicate(menu))
}

const AsideMenuData: React.FC = () => {
  const user = useGetProfile()
  const menus = useMenus(user.data)
  const generated = useMemo(() => filterMenus(menus, (menu) => !Boolean(menu.hidden)), [menus])

  return (
    <>
      {generated?.map((child) => {
        return <GeneratedMenu menu={child} key={child.id} />
      })}
    </>
  )
}

export default AsideMenuData
